"use client";

import * as React from "react";
import { LANGUAGES, NATIVE_LANGUAGES, UI_LOCALE_LANGUAGES } from "@/lib/constants";
import { createContext, useCallback, useMemo, useRef, useEffect } from "react";
import AudioPlayer from "react-h5-audio-player";
import H5AudioPlayer from 'react-h5-audio-player';

import { useState } from "react";
import { AudioMixer } from "@/components/audio/AudioMixer";

export interface SfxProviderProps {
    children: React.ReactNode;
}

const SFX_CHANNELS = 5;

type SfxContextType = {
    isPlaying: boolean;

    // Play a sound effect (it will find the right channel by name)
    playSfx: (name: string, onStart?: () => void, onEnd?: () => void) => void;

    // Stop a sound effect
    stopSfx: (name: string) => void;

    // Load a sound effect into a channel (0-3)
    loadSfx: (name: string, channel: number) => void;

    // Unload a sound effect from a channel (0-3)
    unloadSfx: (channel: number) => void;

    // Unload all sound effects from all channels
    unloadAllSfx: () => void;

    audioMixer: AudioMixer | null;
};

export const SfxContext = createContext<SfxContextType>({
    isPlaying: false,
    playSfx: async (name: string, onStart?: () => void, onEnd?: () => void) => { },
    stopSfx: async (name: string) => { },
    loadSfx: async (name: string, channel: number) => { },
    unloadSfx: async (channel: number) => { },
    unloadAllSfx: async () => { },
    audioMixer: null,
});

const SILENCE_URL = "/speech/silence.mp3"; // Default 'silent' audio for default player init

export function SfxProvider({ children }: SfxProviderProps) {
    const [isPlaying, setIsPlaying] = useState(false);
    
    // Create refs for each audio channel
    const channelPlayerRefs = useRef<(H5AudioPlayer | null)[]>(
        Array(SFX_CHANNELS).fill(null)
    );

    const audioMixer = useMemo(() => {
        const audioMixer = new AudioMixer();
        audioMixer.initSilenceSound();
        return audioMixer;
    }, []);
    
    // Track what's loaded in each channel and their callbacks
    const [channelSounds, setChannelSounds] = useState<Record<number, string>>({});
    const [channelCallbacks, setChannelCallbacks] = useState<Record<number, { onStart?: () => void; onEnd?: () => void }>>({});
    
    // Track which channels are currently playing
    const [playingChannels, setPlayingChannels] = useState<Set<number>>(new Set());

    const loadSfx = useCallback((name: string, channel: number) => {
        audioMixer.preloadSfx(name);
    }, [audioMixer]);

    const unloadSfx = useCallback((channel: number) => {
        if (channel < 0 || channel >= SFX_CHANNELS) {
            console.warn(`Invalid channel ${channel}. Must be between 0 and ${SFX_CHANNELS - 1}`);
            return;
        }

        const player = channelPlayerRefs.current[channel];
        if (player && player.audio && player.audio.current) {
            player.audio.current.pause();
            player.audio.current.src = SILENCE_URL;
            player.audio.current.load();
            
            setChannelSounds(prev => {
                const updated = { ...prev };
                delete updated[channel];
                return updated;
            });
            
            setChannelCallbacks(prev => {
                const updated = { ...prev };
                delete updated[channel];
                return updated;
            });
            
            setPlayingChannels(prev => {
                const updated = new Set(prev);
                updated.delete(channel);
                return updated;
            });
        }
    }, []);

    const unloadAllSfx = useCallback(() => {
        for (let i = 0; i < SFX_CHANNELS; i++) {
            unloadSfx(i);
        }
    }, [unloadSfx]);

    const playSfx = useCallback((url: string, onStart?: () => void, onEnd?: () => void) => {
        audioMixer.playSfx(url, null, onStart, onEnd);
    }, [audioMixer]);

    const stopSfx = useCallback((url: string) => {
        audioMixer.stopSfx(url);
    }, [audioMixer]);

    // Handle when any channel starts playing
    const handleChannelPlay = useCallback((channel: number) => {
        setPlayingChannels(prev => new Set(prev).add(channel));
        setIsPlaying(true);
        
        const callbacks = channelCallbacks[channel];
        if (callbacks?.onStart) {
            callbacks.onStart();
        }
    }, [channelCallbacks]);

    // Handle when any channel ends playing
    const handleChannelEnded = useCallback((channel: number) => {
        setPlayingChannels(prev => {
            const updated = new Set(prev);
            updated.delete(channel);
            return updated;
        });
        
        // If no channels are playing, set isPlaying to false
        setPlayingChannels(currentPlaying => {
            const updated = new Set(currentPlaying);
            updated.delete(channel);
            if (updated.size === 0) {
                setIsPlaying(false);
            }
            return updated;
        });
        
        const callbacks = channelCallbacks[channel];
        if (callbacks?.onEnd) {
            callbacks.onEnd();
        }
    }, [channelCallbacks]);

    useEffect(() => {
        const onUserGesture = () => audioMixer?.resume?.();
        // iOS requires a user gesture to start audio context
        window.addEventListener("touchend", onUserGesture, { once: true });
        window.addEventListener("click", onUserGesture, { once: true });
        return () => {
          // don’t close the context (keeps connections valid)
        };
      }, [audioMixer]);
    
    return (
        <SfxContext.Provider value={{ isPlaying, playSfx, stopSfx, loadSfx, unloadSfx, unloadAllSfx, audioMixer: audioMixer }}>
            {children}

            {/* Hidden audio players for each channel */}
            <div className="hidden">
                {Array.from({ length: SFX_CHANNELS }, (_, index) => (
                    <AudioPlayer
                        key={`sfx-channel-${index}`}
                        src={SILENCE_URL}
                        autoPlayAfterSrcChange={false}
                        autoPlay={false}
                        crossOrigin="anonymous"
                        ref={(ref) => {
                            channelPlayerRefs.current[index] = ref;
                        }}
                        onPlay={() => handleChannelPlay(index)}
                        onEnded={() => handleChannelEnded(index)}
                        onError={(e) => {
                            console.warn(`Audio error on channel ${index}:`, e);
                        }}
                    />
                ))}
            </div>
        </SfxContext.Provider>
    );
}
